import { notification } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { SERVER_STATUS_CODES } from 'src/app/shared/constants/serverResponseStatusCodes.constants'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { useClient } from 'src/app/shared/hooks/useClient'
import { ICreateAccount } from 'src/app/shared/models/accountSetup.model'
import { IClient } from 'src/app/shared/models/clientSetup.model'
import { createAccount } from 'src/app/shared/services/AccountService'
import { getClientById } from 'src/app/shared/services/ClientSetupService'
import { modalRef } from '../components/CustomConfirmationModal'
import { payeeMatchWarningRef } from '../components/PayeeMatchWarningModal'
import { CHECK_ACH_DEFAULTS, PROCESSING_TYPES, defaultValues } from '../constants'
import { NewAccountFormValues } from '../models'
import { NewAccountContext } from './index.context'

export type IDuplicatedAccount = Pick<ICreateAccount.Payload, 'accountName' | 'accountNumber'> & {
  isActive: boolean
  duplicateType: Array<string>
}

export type IDuplicatedAccounts = Array<IDuplicatedAccount>

export type IToastType = 'new' | 'active' | 'archive'

export const NewAccountProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [api, contextHolder] = notification.useNotification()
  const { clients, loadingClients, handleGetClients } = useClient()
  const [loading, setLoading] = useState<boolean>(false)
  const [client, setClient] = useState<IClient | null>(null)
  const form = useForm<NewAccountFormValues>({
    defaultValues: defaultValues,
    mode: 'all',
  })

  const clientId = useWatch({
    control: form.control,
    name: 'clientName',
  })

  const clientName = useMemo(() => clients.find((item) => item.id === clientId)?.clientName ?? '', [clientId, clients])

  const checkExceptionProcessingType = useWatch({
    control: form.control,
    name: 'checkExceptionProcessingType',
  })

  const achExceptionProcessingType = useWatch({
    control: form.control,
    name: 'achExceptionProcessingType',
  })

  const validateForm = useCallback(async () => {
    return await form.trigger()
  }, [form])

  const resetState = useCallback(() => {
    modalRef.current?.hideModal()
  }, [])

  const [activationDateWarning, setActivationDateWarning] = useState<Dayjs | null>(null)

  const formatDataBeforeSubmit = useCallback((data: ICreateAccount.Payload) => {
    return {
      ...data,
      payeeMatch: !!data.payeeMatch,
      accountName: data.accountName.trim(),
      accountNumber: data.accountNumber.trim(),
      checkDecision: data.checkDecision ?? '',
      checkReason: data.checkReason ?? '',
      achDecision: data.achDecision ?? '',
      achReason: data.achReason ?? '',
      staleDatedDays: data?.staleDatedDays ? data.staleDatedDays.toString() : null,
    }
  }, [])

  const handleSetDefaultClientValue = useCallback(
    (client: IClient) => {
      form.setValue('clientName', client.id)
      form.setValue('displayNotes', client.clientNotes)
      form.setValue('checkExceptionProcessingType', client.defaultCheckExceptionCreationType)
      form.setValue('checkDecision', CHECK_ACH_DEFAULTS.checkDecision)
      form.setValue('checkReason', CHECK_ACH_DEFAULTS.checkReason)
      form.setValue('payeeMatch', CHECK_ACH_DEFAULTS.payeeMatch && !!client.payeeMatch)
      form.setValue('achExceptionProcessingType', client.defaultAchExceptionCreationType)
      form.setValue('achDecision', CHECK_ACH_DEFAULTS.achDecision)
      form.setValue('achReason', CHECK_ACH_DEFAULTS.achReason)
      form.setValue('staleDatedDays', CHECK_ACH_DEFAULTS.staleDatedDays)
      setActivationDateWarning(client?.activationDate ? dayjs(client.activationDate) : null)
    },
    [form],
  )

  const handleGoToLandingScreen = useCallback(
    (accountName: string, type: IToastType, showToast: boolean = true) => {
      const message = () => {
        switch (type) {
          case 'active':
            return t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_ACTIVE_SUCCESSFULLY}`, {
              accountName: accountName,
            })
          case 'new':
            return t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_CREATED_SUCCESSFULLY}`, {
              accountName: accountName,
            })
          case 'archive':
            return t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_ARCHIVE_SUCCESSFULLY}`, {
              accountName: accountName,
            })
          default:
            return ''
        }
      }
      navigate(menuUrls.accountSetup, {
        state: [
          {
            displayToast: showToast,
            accountActiveMessage: message(),
          },
        ],
        replace: !showToast,
      })
    },
    [navigate, t],
  )

  const handleCreateConfirm = useCallback(
    (accountName: string) => {
      api.success({
        message: (
          <RichTextField
            richText={t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_CREATED_SUCCESSFULLY}`, {
              accountName: accountName,
            })}
          />
        ),
      })
      form.reset()
      // NOTE: set timeout is used for fixing unset value in dropdown
      client && setTimeout(() => handleSetDefaultClientValue(client), 0)
      modalRef.current?.hideModal()
    },
    [api, client, form, t, handleSetDefaultClientValue],
  )

  const handleGetClient = useCallback(
    async (id: string) => {
      try {
        setLoading(true)
        const clientResponse = await getClientById({
          clientId: id,
        })
        if (clientResponse.statusCode === STATUS_CODES.SUCCESS) {
          setClient(clientResponse.data)
          form.clearErrors()
        }
      } catch (error) {
        console.log('ERROR: ', error)
      } finally {
        setLoading(false)
      }
    },
    [form],
  )

  const onSubmit: SubmitHandler<NewAccountFormValues> = useCallback(
    async (values) => {
      try {
        if (!validateForm()) {
          return
        }
        if (
          // check if processing type of check and ach are all no create
          form.getValues('checkExceptionProcessingType') === PROCESSING_TYPES.NOCREATE &&
          form.getValues('achExceptionProcessingType') === PROCESSING_TYPES.NOCREATE
        ) {
          modalRef.current?.showModal({
            title: t('AccountSetup:new_account_form.confirmation_modal.title'),
            description: t('AccountSetup:new_account_form.confirmation_modal.no_exceptions_description'),
            action: modalRef.current?.hideModal,
            cancel: () =>
              navigate(menuUrls.accountSetup, {
                replace: true,
              }),
          })
        } else {
          setLoading(true)
          const data: NewAccountFormValues = formatDataBeforeSubmit({
            ...values,
            clientName,
          })
          const accountResponse = await createAccount(data)
          if (!accountResponse.data.isSuccess) {
            // NOTE: adjustment GBBM-220
            // NOTE: duplicated account number with inactive one and belonged to same client
            navigate(`${menuUrls.editAccount}/${accountResponse.data.duplicateId}`)
          } else {
            modalRef.current?.showModal({
              title: t('AccountSetup:new_account_form.confirmation_modal.title'),
              description: t('AccountSetup:new_account_form.confirmation_modal.new_description'),
              action: () => handleCreateConfirm(data.accountName),
              cancel: () => handleGoToLandingScreen(data.accountName, 'new'),
              clickCloseIcon: () => handleCreateConfirm(data.accountName),
            })
          }
        }
      } catch (error: any) {
        // inline error message
        if ([SYSTEM_MESSAGE_IDS.ACCOUNT_IN_SYSTEM].includes(error.statusCode)) {
          form.setError('accountName', {
            message: t(`SystemMessages:${error.statusCode}`),
            type: 'pattern',
          })
        }

        if (
          [SYSTEM_MESSAGE_IDS.ACCOUNT_ACTIVE, SYSTEM_MESSAGE_IDS.ACCOUNT_BELONGS_TO_ANOTHER_CLIENT].includes(
            error.statusCode,
          )
        ) {
          form.setError('accountNumber', {
            message: t(`SystemMessages:${error.statusCode}`),
            type: 'pattern',
          })
        }

        // noti error message
        if (error.statusCode === SERVER_STATUS_CODES.PPI10_CLIENT_ARCHIVE) {
          api.error({
            message: '',
            description: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CLIENT_ARCHIVED}`),
            placement: 'topRight',
          })
        }

        if (error.statusCode === SYSTEM_MESSAGE_IDS.ACCOUNT_BELONGS_TO_ANOTHER_CLIENT) {
          api.error({
            message: '',
            description: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_BELONGS_TO_ANOTHER_CLIENT}`),
            placement: 'topRight',
          })
        }
      } finally {
        setLoading(false)
      }
    },
    [
      validateForm,
      form,
      t,
      navigate,
      formatDataBeforeSubmit,
      clientName,
      handleCreateConfirm,
      handleGoToLandingScreen,
      api,
    ],
  )

  const prevCheckProcessingType = useRef<string | undefined>(undefined)
  const prevAchProcessingType = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (checkExceptionProcessingType === PROCESSING_TYPES.NOCREATE) {
      form.setValue('checkDecision', '')
      form.setValue('checkReason', '')
      form.setValue('staleDatedDays', '')
      form.setValue('payeeMatch', false)
      form.clearErrors('checkDecision')
      form.clearErrors('checkReason')
      form.clearErrors('staleDatedDays')
      form.clearErrors('payeeMatch')
    } else if (
      prevCheckProcessingType.current === PROCESSING_TYPES.NOCREATE &&
      checkExceptionProcessingType === PROCESSING_TYPES.CREATE
    ) {
      form.setValue('checkDecision', CHECK_ACH_DEFAULTS.checkDecision)
      form.setValue('checkReason', CHECK_ACH_DEFAULTS.checkReason)
      form.setValue('staleDatedDays', CHECK_ACH_DEFAULTS.staleDatedDays)
      form.setValue('payeeMatch', CHECK_ACH_DEFAULTS.payeeMatch && !!client?.payeeMatch)
    }
    prevCheckProcessingType.current = checkExceptionProcessingType
  }, [checkExceptionProcessingType, client, form, form.setValue])

  useEffect(() => {
    if (achExceptionProcessingType === PROCESSING_TYPES.NOCREATE) {
      form.setValue('achDecision', '')
      form.setValue('achReason', '')
      form.clearErrors('achDecision')
      form.clearErrors('achReason')
    } else if (
      prevAchProcessingType.current === PROCESSING_TYPES.NOCREATE &&
      achExceptionProcessingType === PROCESSING_TYPES.CREATE
    ) {
      form.setValue('achDecision', CHECK_ACH_DEFAULTS.achDecision)
      form.setValue('achReason', CHECK_ACH_DEFAULTS.achReason)
    }
    prevAchProcessingType.current = achExceptionProcessingType
  }, [achExceptionProcessingType, form, form.setValue])

  // set service type parameter
  useEffect(() => {
    const isCreatingCheck = checkExceptionProcessingType === PROCESSING_TYPES.CREATE
    const isCreatingACH = achExceptionProcessingType === PROCESSING_TYPES.CREATE

    if (isCreatingCheck && isCreatingACH) {
      form.setValue('serviceTypeParameter', 'Both')
    } else if (isCreatingCheck && !isCreatingACH) {
      // check only
      form.setValue('serviceTypeParameter', 'Check')
    } else if (!isCreatingCheck && isCreatingACH) {
      // ACH only
      form.setValue('serviceTypeParameter', 'ACH')
    } else {
      form.setValue('serviceTypeParameter', '')
    }
  }, [achExceptionProcessingType, checkExceptionProcessingType, form, t])

  // get list of client when going to this page
  useEffect(() => {
    handleGetClients(true)
  }, [handleGetClients])

  // get client detail after selecting client name
  useEffect(() => {
    if (!!clientId) {
      handleGetClient(clientId)
    }
  }, [clientId, handleGetClient])

  // set client setup data to form after fetching client data
  useEffect(() => {
    if (client) {
      handleSetDefaultClientValue(client)
    }
  }, [client, handleSetDefaultClientValue])

  // set loading in modal
  useEffect(() => {
    modalRef.current?.setLoading(loading)
  }, [loading])

  // reset all state when page is destroyed
  useEffect(() => {
    return () => {
      resetState()
    }
  }, [resetState])

  return (
    <>
      {contextHolder}
      <NewAccountContext.Provider
        value={{
          form,
          checkExceptionProcessingType,
          achExceptionProcessingType,
          clientList: clients,
          loading: loading || loadingClients,
          client,
          modalRef,
          payeeMatchWarningRef,
          onSubmit,
          activationDateWarning,
        }}
      >
        {children}
      </NewAccountContext.Provider>
    </>
  )
}

export default NewAccountProvider
